import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ExpandIcon } from '../../icons/expand-lines.svg';
import { ReactComponent as ClockIcon } from '../../icons/clock-rotate-right.svg';
import { ReactComponent as BoldIcon } from '../../icons/bold.svg';
import { ReactComponent as ItalicIcon } from '../../icons/italic.svg';
import { ReactComponent as UnderlineIcon } from '../../icons/underline.svg';
import { ReactComponent as ArrowUp } from '../../icons/arrow-up.svg';
import { ReactComponent as ArrowDown } from '../../icons/arrow-down.svg';
import { ReactComponent as PeopleIcon } from '../../icons/group.svg';
import { ReactComponent as MidiIcon } from '../../icons/music-double-note-plus.svg';
import { ReactComponent as NoteIcon } from '../../icons/notes.svg';
import { ReactComponent as ChordBarsIcon } from '../../icons/chord-bars.svg';
import { ReactComponent as IntensityIcon } from '../../icons/intensity.svg';
import { ReactComponent as CopyIcon } from '../../icons/copy.svg';
import { ReactComponent as EraseIcon } from '../../icons/erase.svg';

import {
  handleExpandButton,
  handleMakeNoteButton,
  handleBoldButton,
  handleItalicButton,
  handleUnderlineButton,
  handleTransposeUpButton,
  handleTransposeDownButton,
} from '../../helpers/lyricsActions';
import { findChords } from '../../utils/chordPatterns';
import { parseBarLines } from '../../utils/chordBars';
import { canChart as canChartPart } from '../../utils/parts';

import Icon from '../common/Icon';
import PartKeyPicker from '../PartKeyPicker/PartKeyPicker';
import PartBpmPicker from '../PartBpmPicker/PartBpmPicker';

import './LyricsActions.scss';

const REPEAT_OPTIONS = Array.from({ length: 11 }, (_, i) => i + 2); // 2..12
const TEXT_AREA_ID = 'currentLyrics';
const MOD_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';

const hasTextAreaSelection = () => {
  const $textArea = document.getElementById(TEXT_AREA_ID);

  return !!$textArea && $textArea.selectionStart !== $textArea.selectionEnd;
};

const LyricsActions = ({
  setInputToState,
  currentKey,
  bpm,
  onOpenTimerModal,
  editPartId,
  currentLyrics,
  onOpenAssignModal,
  editingPartHasAssignments,
  onOpenMidiModal,
  editingPartHasMidi,
  repeatCount,
  onRepeatCountChange,
  partKey,
  partKeyOptions,
  partKeyInForce,
  onPartKeyChange,
  partBpm,
  songBpm,
  onPartBpmChange,
  isDuplicatePart = false,
  currentPart,
  onOpenChordBarsModal,
  editingPartHasChart = false,
  onTransposeChart,
  onOpenInIntensity,
  onCopyLyrics,
  onClearLyrics,
}) => {
  const { t } = useTranslation();
  const [hasSelection, setHasSelection] = useState(false);

  // The format buttons only act on a selection, so they follow the textarea's as it changes
  useEffect(() => {
    const update = () => setHasSelection(hasTextAreaSelection());

    update();
    document.addEventListener('selectionchange', update);
    document.addEventListener('select', update, true);

    return () => {
      document.removeEventListener('selectionchange', update);
      document.removeEventListener('select', update, true);
    };
  }, [currentLyrics]);
  const canAttach = !!(editPartId || currentLyrics?.trim());
  const hasText = !!currentLyrics?.trim();
  // A charted part can be saved with no words, so it can still repeat and carry a key and tempo
  const hasContent = hasText || editingPartHasChart;
  // A part keeping its chart off its lyrics has chords to move even where none are written in
  // them, so the buttons stay live for it
  const canTranspose = !!currentKey && hasText && (findChords(currentLyrics).length > 0 || editingPartHasChart);
  // An instrumental part writes its chart into its own lyrics; a part made of words keeps one on
  // the part instead. Both are played at the song's tempo, so neither can be built without one.
  const canChart = canChartPart(currentPart);
  const canBuildChordBars = canChart && Number(bpm) > 0;
  const canSetPartKey = !!onPartKeyChange && hasContent && currentPart !== 'Section' && !isDuplicatePart;
  const canSetPartBpm = canSetPartKey && Number(songBpm) > 0;
  const hasChordBars = !!parseBarLines(currentLyrics) || editingPartHasChart;
  const chordBarsTitle = canBuildChordBars
    ? t('dialogs.chordBars.title')
    : canChart
      ? t('songForm.lyricsActions.chordBarsNoBpm')
      : t('songForm.lyricsActions.chordBarsNotHere');

  const textButtons = [
    {
      title: t('songForm.lyricsActions.bold', { shortcut: `${MOD_KEY}B` }),
      icon: <BoldIcon />,
      onClick: () => handleBoldButton(setInputToState),
      disabled: !hasSelection,
    },
    {
      title: t('songForm.lyricsActions.italic', { shortcut: `${MOD_KEY}I` }),
      icon: <ItalicIcon />,
      onClick: () => handleItalicButton(setInputToState),
      disabled: !hasSelection,
    },
    {
      title: t('songForm.lyricsActions.underline', { shortcut: `${MOD_KEY}U` }),
      icon: <UnderlineIcon />,
      onClick: () => handleUnderlineButton(setInputToState),
      disabled: !hasSelection,
    },
    {
      title: t('songForm.lyricsActions.makeNote'),
      icon: <NoteIcon />,
      onClick: () => handleMakeNoteButton(setInputToState),
      disabled: !hasSelection,
    },
    {
      title: t('songForm.lyricsActions.expandLines'),
      icon: <ExpandIcon />,
      onClick: () => handleExpandButton(setInputToState),
      disabled: !hasSelection,
    },
    {
      title: t('songForm.openInIntensity'),
      icon: <IntensityIcon />,
      onClick: onOpenInIntensity,
      disabled: !onOpenInIntensity,
    },
    {
      title: t('songForm.copyLyrics'),
      icon: <CopyIcon />,
      onClick: onCopyLyrics,
      disabled: !hasText,
    },
    {
      title: t('songForm.clearLyrics'),
      icon: <EraseIcon />,
      onClick: onClearLyrics,
      disabled: !hasText,
    },
  ];

  const buttons = [
    {
      title: t('songForm.lyricsActions.assign'),
      icon: <PeopleIcon />,
      onClick: onOpenAssignModal,
      disabled: !canAttach,
      className: 'btn-assign',
      children: editingPartHasAssignments && <span className="btn-dot" />,
    },
    {
      title: chordBarsTitle,
      icon: <ChordBarsIcon />,
      onClick: onOpenChordBarsModal,
      disabled: !canBuildChordBars,
      className: 'btn-chord-bars',
      children: hasChordBars && <span className="btn-dot" />,
    },
    {
      title: t('songForm.lyricsActions.midiFile'),
      icon: <MidiIcon />,
      onClick: onOpenMidiModal,
      disabled: !canAttach || !currentKey,
      className: 'btn-midi',
      children: editingPartHasMidi && <span className="btn-dot" />,
    },
    {
      title: t('dialogs.repeatTimer.title'),
      icon: <ClockIcon />,
      onClick: onOpenTimerModal,
      disabled: !hasText || !(Number(bpm) > 0),
    },
    // The chart goes with them: a part keeping one off its lyrics has chords in two places, and
    // the button only reaches the textarea
    {
      title: t('songForm.lyricsActions.transposeUp'),
      icon: <ArrowUp />,
      onClick: () => {
        handleTransposeUpButton(setInputToState, currentKey);
        onTransposeChart?.('up');
      },
      disabled: !canTranspose,
    },
    {
      title: t('songForm.lyricsActions.transposeDown'),
      icon: <ArrowDown />,
      onClick: () => {
        handleTransposeDownButton(setInputToState, currentKey);
        onTransposeChart?.('down');
      },
      disabled: !canTranspose,
    },
  ];

  return (
    <div className="LyricsActions">
      <div className="actions-row horizontal-menu">
        <select
          className="repeat-select"
          title={t('songForm.lyricsActions.repeatTitle')}
          value={repeatCount || 1}
          disabled={!hasContent}
          onChange={(e) => onRepeatCountChange(Number(e.target.value))}
        >
          <option value={1}>{t('songForm.lyricsActions.noRepeat')}</option>
          {REPEAT_OPTIONS.map((n) => (
            <option key={n} value={n}>
              ×{n}
            </option>
          ))}
        </select>
        {partKeyOptions && (
          <PartKeyPicker
            options={partKeyOptions}
            value={partKey}
            inForceKey={partKeyInForce}
            disabled={!canSetPartKey}
            title={
              isDuplicatePart ? t('songForm.lyricsActions.partKeyDuplicate') : t('songForm.lyricsActions.partKeyTitle')
            }
            onChange={onPartKeyChange}
          />
        )}
        {onPartBpmChange && (
          <PartBpmPicker
            value={partBpm}
            songBpm={songBpm}
            disabled={!canSetPartBpm}
            title={
              isDuplicatePart
                ? t('songForm.lyricsActions.partBpmDuplicate')
                : Number(songBpm) > 0
                  ? t('songForm.lyricsActions.partBpmTitle')
                  : t('songForm.lyricsActions.setSongBpm')
            }
            onChange={onPartBpmChange}
          />
        )}
        {buttons.map(({ title, icon, onClick, disabled, className, children }) => (
          <button
            key={title}
            type="button"
            className={cx('btn', className)}
            title={title}
            disabled={disabled}
            onClick={onClick}
          >
            <Icon icon={icon} />
            {children}
          </button>
        ))}
      </div>
      {/* Kept off the textarea's focus, so tapping one doesn't drop the selection it acts on */}
      <div className="format-row horizontal-menu">
        {textButtons.map(({ title, icon, onClick, disabled }) => (
          <button
            type="button"
            key={title}
            className="btn"
            title={title}
            aria-label={title}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
          >
            <Icon icon={icon} />
          </button>
        ))}
      </div>
    </div>
  );
};

LyricsActions.propTypes = {
  setInputToState: PropTypes.func.isRequired,
  currentKey: PropTypes.string,
  bpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onOpenTimerModal: PropTypes.func,
  editPartId: PropTypes.string,
  currentLyrics: PropTypes.string,
  onOpenAssignModal: PropTypes.func,
  editingPartHasAssignments: PropTypes.bool,
  onOpenMidiModal: PropTypes.func,
  editingPartHasMidi: PropTypes.bool,
  repeatCount: PropTypes.number,
  onRepeatCountChange: PropTypes.func,
  partKey: PropTypes.string,
  partKeyOptions: PropTypes.shape({
    songOption: PropTypes.shape({ label: PropTypes.string.isRequired, hint: PropTypes.string }).isRequired,
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
        ).isRequired,
      }),
    ).isRequired,
  }),
  partKeyInForce: PropTypes.string,
  onPartKeyChange: PropTypes.func,
  partBpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  songBpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPartBpmChange: PropTypes.func,
  isDuplicatePart: PropTypes.bool,
  currentPart: PropTypes.string,
  onOpenChordBarsModal: PropTypes.func,
  editingPartHasChart: PropTypes.bool,
  onTransposeChart: PropTypes.func,
  onOpenInIntensity: PropTypes.func,
  onCopyLyrics: PropTypes.func,
  onClearLyrics: PropTypes.func,
};

export default LyricsActions;
